import Axios from 'axios';
import * as AccountRequest from '@/models/accountRequest';
import * as AccountResponse from '@/models/accountResponse';
import { UserTypes } from '@/enums/userTypes';

export type Tutor = AccountResponse.TutorAccountDetailResponse;

export interface SearchCondition {
  authority: UserTypes;
  keyWord: string;
  perPage: number;
  page: number;
  sort: string;
}

interface State {
  loading: boolean;
  countAll: number;
  users: Tutor[];
  searchCondition: SearchCondition;
}
const mapState: State = {
  loading: false,
  countAll: 0,
  users: new Array<Tutor>(),
  searchCondition: {
    authority: UserTypes.All,
    keyWord: '',
    perPage: 20,
    page: 1,
    sort: '',
  },
};

const mapActions = {
  async searchTutors({ commit }: any, searchCondition: SearchCondition) {
    commit('setLoading', true);
    const request: AccountRequest.TutorAccountRequest = {
      id: undefined,
      userId: undefined,
      lastName: undefined,
      firstName: undefined,
      lastNameKana: undefined,
      firstNameKana: undefined,
      note: undefined,
      subjectIdList: undefined,
      schoolTypeList: undefined,
      authority: searchCondition.authority,
      keyWord: searchCondition.keyWord,
      perPage: searchCondition.perPage,
      page: searchCondition.page,
      sort: searchCondition.sort,
    };
    const response = await Axios.post('/api/Account/TutorSearch', request).catch((error) => {
      commit('setLoading', false);
      throw error;
    });
    commit('searchTutors', response.data);
  },
  async createTutor({ commit }: any, user: Tutor) {
    if (!user || !user.lastName || !user.firstName) {
      throw new Error('氏名を入力してください。');
    }
    if (!user.lastNameKana || !user.firstNameKana) {
      throw new Error('カナ氏名を入力してください。');
    }
    const request: AccountRequest.TutorAccountRequest = {
      id: undefined,
      userId: user.userId,
      lastName: user.lastName,
      firstName: user.firstName,
      lastNameKana: user.lastNameKana,
      firstNameKana: user.firstNameKana,
      note: user.note,
      subjectIdList: user.subjectList.map((subject) => {
        return subject.id;
      }),
      schoolTypeList: user.schoolTypeList,
      authority: user.authority,
      keyWord: undefined,
      perPage: undefined,
      page: undefined,
      sort: undefined,
    };
    const response = await Axios.post('/api/Account/TutorCreate', request).catch((error) => {
      throw new Error('講師の登録に失敗しました。');
    });
    return response.data;
  },
  async editTutor({ commit }: any, user: Tutor) {
    if (!user || !user.id) {
      throw new Error('変更する講師を選択してください。');
    }
    if (!user || !user.lastName || !user.firstName) {
      throw new Error('氏名を入力してください。');
    }
    if (!user.lastNameKana || !user.firstNameKana) {
      throw new Error('カナ氏名を入力してください。');
    }
    const request: AccountRequest.TutorAccountRequest = {
      id: user.id,
      userId: user.userId,
      lastName: user.lastName,
      firstName: user.firstName,
      lastNameKana: user.lastNameKana,
      firstNameKana: user.firstNameKana,
      note: user.note,
      subjectIdList: user.subjectList.map((subject) => {
        return subject.id;
      }),
      schoolTypeList:  user.schoolTypeList,
      authority: user.authority,
      keyWord: undefined,
      perPage: undefined,
      page: undefined,
      sort: undefined,
    };
    const response = await Axios.post('/api/Account/TutorEdit', request).catch((error) => {
      throw new Error('講師の変更に失敗しました。');
    });
    commit('changeTutor', user);
    return response.data;
  },
  async deleteTutor({ commit }: any, id: string) {
    if (!id) {
      throw new Error('削除する講師を選択してください。');
    }
    const request: AccountRequest.TutorAccountRequest = {
      id,
      userId: undefined,
      lastName: undefined,
      firstName: undefined,
      lastNameKana: undefined,
      firstNameKana: undefined,
      note: undefined,
      subjectIdList: undefined,
      schoolTypeList: undefined,
      authority: undefined,
      keyWord: undefined,
      perPage: undefined,
      page: undefined,
      sort: undefined,
    };
    const response = await Axios.post('/api/Account/TutorDelete', request).catch((error) => {
      throw new Error('講師の削除に失敗しました。');
    });
    commit('removeTutor', id);
    return response.data;
  },
  async resetPassword({ commit }: any, id: string) {
    if (!id) {
      throw new Error('パスワード初期化する講師を選択してください。');
    }
    const request: AccountRequest.TutorAccountRequest = {
      id,
      userId: undefined,
      lastName: undefined,
      firstName: undefined,
      lastNameKana: undefined,
      firstNameKana: undefined,
      note: undefined,
      subjectIdList: undefined,
      schoolTypeList: undefined,
      authority: undefined,
      keyWord: undefined,
      perPage: undefined,
      page: undefined,
      sort: undefined,
    };
    const response = await Axios.post('/api/Account/TutorReset', request).catch((error) => {
      throw new Error('パスワードの初期化に失敗しました。');
    });
    return response.data;
  },
  async getTutor({ commit }: any, id: string) {
    const request: AccountRequest.TutorAccountRequest = {
      id,
      userId: undefined,
      lastName: undefined,
      firstName: undefined,
      lastNameKana: undefined,
      firstNameKana: undefined,
      note: undefined,
      subjectIdList: undefined,
      schoolTypeList: undefined,
      authority: undefined,
      keyWord: undefined,
      perPage: undefined,
      page: undefined,
      sort: undefined,
    };
    const response = await Axios.post('/api/Account/TutorDetail', request).catch((error) => {
      throw error;
    });
    return response.data as Tutor;
  },
};

const mapMutations = {
  setLoading(state: State, loading: boolean) {
    state.loading = loading;
  },
  searchTutors(state: State, data: AccountResponse.TutorAccountListResponse) {
    state.countAll = data.countAll;
    state.users = data.accountList;
    state.loading = false;
  },
  changeTutor(state: State, user: Tutor) {
    const findIndex = state.users.findIndex((value) => {
      return (value.id === user.id);
    });
    if (findIndex >= 0) {
      state.users.splice(findIndex, 1, user);
    }
  },
  removeTutor(state: State, id: string) {
    const findIndex = state.users.findIndex((value) => {
      return (value.id === id);
    });
    if (findIndex >= 0) {
      state.users.splice(findIndex, 1);
    }
  },
};

export default {
  namespaced: true,
  state: mapState,
  actions: mapActions,
  mutations: mapMutations,
};
